/**
 * Day-level date helpers for schedule views — pure, no React, no domain knowledge.
 *
 * All arithmetic is in **local** time, and every parse goes through `parseDayIso` rather than
 * `new Date(dayIso)` — the latter reads a bare `YYYY-MM-DD` as UTC midnight, which lands on the
 * previous day west of Greenwich.
 */

/** `YYYY-MM-DD` for a local date. */
export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Local midnight of a `YYYY-MM-DD` string, or `null` if it does not parse. */
export function parseDayIso(dayIso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dayIso);
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
}

/** Shifts a `YYYY-MM-DD` string by whole days, staying in local time. */
export function addDays(dayIso: string, days: number): string {
  const date = parseDayIso(dayIso) ?? new Date();
  date.setDate(date.getDate() + days);
  return toIsoDate(date);
}

/** Saturday or Sunday. */
export function isWeekend(dayIso: string): boolean {
  const date = parseDayIso(dayIso);
  if (!date) return false;
  const dow = date.getDay();
  return dow === 0 || dow === 6;
}

/** The same day, or the following Monday when it falls on a weekend. */
export function toWeekday(dayIso: string): string {
  let day = dayIso;
  // Bounded: two steps at most, from Saturday.
  while (isWeekend(day)) day = addDays(day, 1);
  return day;
}

/**
 * One day forwards or backwards, skipping the weekend — so `next` on a Friday lands on Monday and
 * `prev` on a Monday lands on Friday. The board is a working-week tool; there is nothing to
 * disponieren on a Sunday.
 */
export function addWeekday(dayIso: string, direction: 1 | -1): string {
  let day = addDays(dayIso, direction);
  while (isWeekend(day)) day = addDays(day, direction);
  return day;
}

/**
 * The `from` / `to` pair for `GET /schedule`. Covers the whole local day, not just the drawn
 * window, so an entry starting at 05:30 still arrives and can be clipped into view.
 */
export function dayBoundsIso(dayIso: string): { from: string; to: string } {
  const start = parseDayIso(dayIso) ?? new Date();
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
}

/** Days drawn by the week view: Montag … Freitag. */
export const WEEK_DAY_COUNT = 5;

/**
 * Monday of the week containing `dayIso` — ISO weeks, so Sunday belongs to the week that just
 * ended, not the one about to start.
 */
export function startOfWeekIso(dayIso: string): string {
  const date = parseDayIso(dayIso) ?? new Date();
  const dow = date.getDay(); // 0 = Sunday
  return addDays(toIsoDate(date), dow === 0 ? -6 : 1 - dow);
}

/** The five weekdays Mo…Fr of the week containing `dayIso`. */
export function weekdaysOfWeek(dayIso: string): string[] {
  const monday = startOfWeekIso(dayIso);
  return Array.from({ length: WEEK_DAY_COUNT }, (_, i) => addDays(monday, i));
}

/**
 * The `from` / `to` pair for `GET /schedule` over a whole working week — Monday 00:00 to Friday
 * 23:59:59.999, local. Comfortably inside the server's `MAX_SCHEDULE_RANGE_DAYS` of 62.
 */
export function weekBoundsIso(dayIso: string): { from: string; to: string } {
  const days = weekdaysOfWeek(dayIso);
  const start = parseDayIso(days[0]) ?? new Date();
  const end = parseDayIso(days[days.length - 1]) ?? new Date();
  end.setHours(23, 59, 59, 999);
  return { from: start.toISOString(), to: end.toISOString() };
}

/**
 * ISO-8601 calendar week number. Used for the week nav's `KW 34` label.
 *
 * The Thursday rule: the week containing the year's first Thursday is week 1, so the calculation
 * hops to the Thursday of `dayIso`'s week before counting.
 */
export function isoWeekNumber(dayIso: string): number {
  const date = parseDayIso(dayIso) ?? new Date();
  const dow = (date.getDay() + 6) % 7; // 0 = Monday
  const thursday = new Date(date);
  thursday.setDate(date.getDate() - dow + 3);

  const firstThursday = new Date(thursday.getFullYear(), 0, 4);
  const firstDow = (firstThursday.getDay() + 6) % 7;
  firstThursday.setDate(firstThursday.getDate() - firstDow + 3);

  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  return 1 + Math.round((thursday.getTime() - firstThursday.getTime()) / msPerWeek);
}
